/**
 * Schema lens — the UNION of the batch's items, with presence.
 *
 * Replaces a first-row walk that carried the banner "fields present only on
 * later items aren't surfaced" — an honest label on a view that HIDES the
 * optional field you opened the schema to find. The inference lives in
 * `lib/workflows/schemaInfer.ts` (pure, tested); this only renders it.
 *
 * What each row tells you, in the order you ask it:
 *   name · type(s) · "3/5" when it isn't on every item · example value
 *
 * IT IS ALSO THE BINDING PICKER (n8n model). When `exprBase` is set, every
 * field row carries its `{{ <base><path> }}` binding: DRAG it into an ƒx
 * field (payload = `text/plain` + BINDING_DRAG_MIME), or CLICK a leaf to
 * insert into the last-focused ƒx field via `onPick`. A branch row keeps its
 * click for expand/collapse — drag is how you bind an object/array whole.
 */

import * as React from "react";

import {
  Braces,
  Brackets,
  ChevronDown,
  ChevronRight,
  GripVertical,
  Hash,
  ToggleLeft,
  Type,
} from "lucide-react";

import { cn } from "@/lib/cn";
import {
  type InferredSchema,
  type SchemaField,
  type SchemaKind,
  isOptional,
} from "@/lib/workflows/schemaInfer";
import { BINDING_DRAG_MIME, accessFor } from "@/components/workflows/expressionPicker";

const KIND_ICON: Record<SchemaKind, typeof Hash> = {
  object: Braces,
  array: Brackets,
  string: Type,
  number: Hash,
  boolean: ToggleLeft,
  null: Type,
};

const KIND_TONE: Record<SchemaKind, string> = {
  object: "text-muted-foreground",
  array: "text-muted-foreground",
  string: "text-success",
  number: "text-info",
  boolean: "text-warning",
  null: "text-muted-foreground",
};

/** One-line example. Long strings are cut — the JSON lens is where you read a
 *  whole value; here it's a hint that the field is what you think it is. */
function sampleText(v: unknown): string {
  if (v === undefined) return "";
  if (v === null) return "null";
  if (typeof v === "string") return `"${v.length > 40 ? v.slice(0, 37) + "…" : v}"`;
  if (Array.isArray(v)) return `[${v.length}]`;
  if (typeof v === "object") return `{${Object.keys(v).length}}`;
  return String(v);
}

/** The expression body for a CHILD of `parentExpr`. The `[]` element row binds
 *  the representative element (`[0]`) — the same convention n8n emits when you
 *  drag an array field. */
function childExpr(parentExpr: string, field: SchemaField): string {
  return field.key === "[]" ? `${parentExpr}[0]` : parentExpr + accessFor(field.key);
}

function FieldRow({
  field,
  depth,
  expr,
  onPick,
}: {
  field: SchemaField;
  depth: number;
  /** Expression body reaching THIS field (`$json.user.name`), or null when the
   *  lens is view-only (no exprBase — every non-drawer call site). */
  expr: string | null;
  onPick?: (binding: string) => void;
}) {
  const branch = field.children.length > 0;
  const [open, setOpen] = React.useState(depth < 1);
  const primary = field.kinds[0] ?? "null";
  const Icon = KIND_ICON[primary];
  const optional = isOptional(field);
  const binding = expr === null ? null : `{{ ${expr} }}`;

  return (
    <div style={{ paddingLeft: depth === 0 ? 0 : 12 }}>
      <div
        className={cn(
          "group/schemarow flex items-center gap-2 rounded px-1 py-0.5 text-[11px]",
          (branch || (binding && !branch && onPick)) && "cursor-pointer hover:bg-muted/50",
          binding && "cursor-grab active:cursor-grabbing",
        )}
        draggable={!!binding}
        onDragStart={
          binding
            ? (e) => {
                // text/plain makes a drop into any PLAIN input work natively;
                // the custom MIME is what our ƒx drop targets key on.
                e.dataTransfer.setData("text/plain", binding);
                e.dataTransfer.setData(BINDING_DRAG_MIME, binding);
                e.dataTransfer.effectAllowed = "copy";
              }
            : undefined
        }
        onClick={
          branch ? () => setOpen((o) => !o) : binding && onPick ? () => onPick(binding) : undefined
        }
        title={
          binding
            ? `Trascina in un campo ƒx${!branch && onPick ? " (o clicca per inserire)" : ""} — ${binding}`
            : undefined
        }
      >
        {branch ? (
          open ? (
            <ChevronDown className="h-3 w-3 shrink-0 text-muted-foreground" strokeWidth={1.5} />
          ) : (
            <ChevronRight className="h-3 w-3 shrink-0 text-muted-foreground" strokeWidth={1.5} />
          )
        ) : (
          <span className="w-3 shrink-0" />
        )}
        <Icon className={cn("h-3 w-3 shrink-0", KIND_TONE[primary])} strokeWidth={1.5} />
        <code className="shrink-0 font-mono text-foreground">{field.key}</code>
        <span className="shrink-0 text-[10px] text-muted-foreground">
          {field.kinds.join(" | ")}
        </span>
        {optional && (
          // The single most useful fact a batch schema carries: this is what
          // breaks a downstream expression at item #4.
          <span
            className="shrink-0 rounded bg-warning/15 px-1 text-[9px] text-warning"
            title={`Presente su ${field.present} item su ${field.total}`}
          >
            {field.present}/{field.total}
          </span>
        )}
        {!branch && (
          <span className="min-w-0 truncate text-muted-foreground" title={sampleText(field.sample)}>
            {sampleText(field.sample)}
          </span>
        )}
        {binding && (
          <GripVertical
            className="ml-auto h-3 w-3 shrink-0 text-muted-foreground/0 transition group-hover/schemarow:text-muted-foreground/70"
            strokeWidth={1.5}
            aria-hidden
          />
        )}
      </div>
      {branch && open && (
        <div>
          {field.children.map((c) => (
            <FieldRow
              key={c.path}
              field={c}
              depth={depth + 1}
              expr={expr === null ? null : childExpr(expr, c)}
              onPick={onPick}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function SchemaTree({
  schema,
  className,
  exprBase,
  onPick,
}: {
  schema: InferredSchema;
  className?: string;
  /** Expression root the fields bind under (`$json`, `$('Label').item.json`).
   *  Unset ⇒ view-only lens (no drag, no insert) — every non-drawer call site. */
  exprBase?: string;
  /** Click-to-insert into the last-focused ƒx field (the drawer's
   *  `insertBinding` seam). Drag works with `exprBase` alone. */
  onPick?: (binding: string) => void;
}) {
  const optionalCount = schema.fields.filter(isOptional).length;
  return (
    <div className={cn("min-w-0", className)}>
      <div className="mb-1.5 flex flex-wrap items-center gap-2 text-[10px] text-muted-foreground">
        <span>
          {schema.fields.length} {schema.fields.length === 1 ? "campo" : "campi"} · unione di{" "}
          {schema.sampled} {schema.sampled === 1 ? "item" : "item"}
        </span>
        {schema.sampled < schema.total && (
          // Say it, rather than let the union read as exhaustive.
          <span title={`La batch ha ${schema.total} item; ne sono stati letti ${schema.sampled}`}>
            · su {schema.total} (campione)
          </span>
        )}
        {optionalCount > 0 && (
          <span className="text-warning">· {optionalCount} non su tutti gli item</span>
        )}
        {exprBase && <span className="ml-auto italic">trascina un campo in un campo ƒx</span>}
      </div>
      {schema.fields.length === 0 ? (
        // A batch of SCALARS now yields the synthetic `(valore)` field, so this
        // is only the genuinely empty case (no items, or items that are empty
        // objects) — don't say "not objects" here, it would be wrong for both.
        <div className="text-[11px] text-muted-foreground">Nessun campo.</div>
      ) : (
        schema.fields.map((f) => (
          <FieldRow
            key={f.path}
            field={f}
            depth={0}
            // The synthetic scalar field's path is `$` — the item ITSELF, so it
            // binds the bare base (`{{ $json }}`), not a member access.
            expr={
              exprBase === undefined
                ? null
                : f.path === "$"
                  ? exprBase
                  : exprBase + accessFor(f.key)
            }
            onPick={onPick}
          />
        ))
      )}
    </div>
  );
}
